import type { DataChannelMessage } from "../types/messages";

export class DataChannelMessageHandler {
  private readonly handlers: {
    [K in DataChannelMessage["type"]]?: (
      message: Extract<DataChannelMessage, { type: K }>,
    ) => void;
  };

  constructor(handlers: {
    onRumble?: (
      message: Extract<DataChannelMessage, { type: "rumble" }>,
    ) => void;
    onSystem?: (
      message: Extract<DataChannelMessage, { type: "system" }>,
    ) => void;
    onPing?: (message: Extract<DataChannelMessage, { type: "ping" }>) => void;
    onLatencyMeasurement?: (
      message: Extract<DataChannelMessage, { type: "latency_measurement" }>,
    ) => void;
    onSystemStats?: (
      message: Extract<DataChannelMessage, { type: "system_stats" }>,
    ) => void;
    onPipeline?: (
      message: Extract<DataChannelMessage, { type: "pipeline" }>,
    ) => void;
    onClipboard?: (
      message: Extract<DataChannelMessage, { type: "clipboard" }>,
    ) => void;
    onCursor?: (
      message: Extract<DataChannelMessage, { type: "cursor" }>,
    ) => void;
    onGpuStats?: (
      message: Extract<DataChannelMessage, { type: "gpu_stats" }>,
    ) => void;
  }) {
    this.handlers = {
      system: handlers.onSystem,
      ping: handlers.onPing,
      latency_measurement: handlers.onLatencyMeasurement,
      system_stats: handlers.onSystemStats,
      pipeline: handlers.onPipeline,
      clipboard: handlers.onClipboard,
      cursor: handlers.onCursor,
      gpu_stats: handlers.onGpuStats,
      rumble: handlers.onRumble,
    };
  }

  handleMessage(event: MessageEvent): void {
    let message: DataChannelMessage;

    try {
      message = JSON.parse(event.data);
      if (!this.isValidMessage(message)) {
        throw new Error("Invalid message format");
      }
    } catch (error) {
      console.error("Error parsing data channel message:", error);
      console.error("Raw message:", event.data);
      return;
    }

    try {
      const handler = this.handlers[message.type];
      if (handler) {
        // TypeScript type inference works correctly here due to our type guard
        handler(message as any);
      }
    } catch (error) {
      console.error(`Error handling ${message.type} message:`, error);
    }
  }

  private isValidMessage(message: any): message is DataChannelMessage {
    return (
      message &&
      typeof message === "object" &&
      typeof message.type === "string" &&
      Object.keys(this.handlers).includes(message.type)
    );
  }
}
